<script lang="ts" setup>
import { InputTypeEnum } from "@/enums/chatEnums";
import i18n from "@/locales";
import { useMate } from "@/stores/mate";
import { ChatMessage, Mate } from "@/types/entity";
import { Field, FieldInstance } from "vant";
import { computed, ref } from "vue";

import { useMateDispatchMessage } from "@/stores/mateDispatchMessage";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    mate?: Mate;
    dispatchUuid: string;
    inputMsg: string;
    myMessage: string;
    isStreamReplying: boolean;
    streamMsg: string;
    isPostingMessage: "start" | "end" | false;
    showAutoPlayStatus?: boolean;
    placeholder?: string;
    fromName?: string;
    toName?: string[];
    inputType: InputTypeEnum;
    temperature?: number;
    enableShowBottomOptions?: boolean;
    enableFastForward?: boolean;
  }>(),
  {
    toName: () => [],
    showAutoPlayStatus: true,
    enableShowBottomOptions: true,
    enableFastForward: true,
  }
);
const emit = defineEmits<{
  (e: "focus", event: FocusEvent): void;
  (e: "blur"): void;
  (e: "update:inputMsg", val: string): void;
  (e: "update:myMessage", val: string): void;
  (e: "update:isStreamReplying", val: boolean): void;
  (e: "update:streamMsg", val: string): void;
  (
    e: "onStreamFinish",
    params: {
      userMsg?: ChatMessage;
      assistantMsg?: ChatMessage;
    }
  ): void;
  (e: "onPostMsgClick", toNames?: string[]): void;
  (e: "update:isPostingMessage", val: "start" | "end" | false): void;
}>();

const mateStore = useMate();
const dispatchMessageStore = useMateDispatchMessage();

const enableAgentChat = computed<boolean>(() => false);
const inputRef = ref<FieldInstance | null>(null);
const isPrivate = ref(false);
const isInputDisabled = computed(
  () => !!props.isPostingMessage || (!isPrivate.value && !props.inputMsg.trim())
);
const isMessageListEmpty = computed(() => {
  return (
    dispatchMessageStore.messageListByDispatchUuid(props.dispatchUuid)
      .length === 0
  );
});

// EventHandler
const postMessage = async ({
  force = false,
  text,
  loadingPosition = "end",
}: {
  force?: boolean;
  text?: string;
  loadingPosition?: "start" | "end";
} = {}) => {
  if (props.isPostingMessage && !force) {
    return;
  }
  if (!props.inputMsg.trim() && !text?.trim()) {
    return;
  }
  loadingPosition =
    loadingPosition ??
    (props.inputMsg ||
    dispatchMessageStore.getMsgByIdx(props.dispatchUuid, 0)?.mateId ===
      mateStore.currentMate?.id
      ? "start"
      : "end");
  emit("onPostMsgClick");
  emit("update:isPostingMessage", loadingPosition);
  const tmpVal = text ?? props.inputMsg;
  emit("update:myMessage", tmpVal);
  emit("update:inputMsg", "");
  // inputRef.value!.focus();
  try {
    dispatchMessageStore.fetchingFlag = true;
    await dispatchMessageStore.sendMessage({
      dispatch: props.dispatchUuid,
      text: tmpVal,
    });
    emit("update:myMessage", "");
    await dispatchMessageStore.getNewestMessage(props.dispatchUuid);
  } catch (e) {
    emit("update:myMessage", tmpVal);
    throw e;
  } finally {
    emit("update:isPostingMessage", false);
    dispatchMessageStore.fetchingFlag = false;
  }
};

const mode = ref("text");

export interface ChatInputInstance {
  inputRef: FieldInstance | null;
  postMessage: typeof postMessage;
}

defineExpose({ inputRef, postMessage });
</script>
<template>
  <div
    class="w-full px-2 pb-2 mt-2 box-border relative flex flex-col transition-all duration-300 gap-2"
  >
    <div
      v-if="mode === 'text'"
      class="flex items-center justify-between h-full rounded-lg overflow-hidden"
    >
      <div
        v-if="!isMessageListEmpty"
        class="flex items-end gap-0.5 flex-grow backdrop-blur-lg"
      >
        <div
          :class="[
            'flex items-center justify-between flex-grow gap-1 rounded-lg',
            enableAgentChat ? 'bg-none' : 'bg-neutral text-neutral-content',
          ]"
        >
          <slot></slot>
          <div class="flex w-full gap-1 items-center justify-between px-2">
            <div class="flex items-end flex-grow rounded">
              <field
                ref="inputRef"
                :disabled="enableAgentChat"
                :enterkeyhint="$t('Chats.send')"
                :model-value="inputMsg"
                :placeholder="
                  enableAgentChat
                    ? i18n.global.t('Chats.proxyEnabled')
                    : placeholder ??
                      i18n.global.t('Chats.sendMsgTo', { name: mate?.name })
                "
                :style="{
                  '--van-field-input-text-color': 'white',
                }"
                autosize
                class="pl-2 pr-2 flex-1 after:hidden bg-transparent max-h-[80px] overflow-y-auto"
                rows="1"
                type="textarea"
                @blur="emit('blur')"
                @focus="emit('focus', $event)"
                @update:model-value="emit('update:inputMsg', $event)"
                @keydown.enter.prevent.stop="postMessage()"
              />
            </div>
            <div class="flex text-base-content/50 items-center">
              <div
                :disabled="isInputDisabled"
                class="mt-1 text-zinc-300"
                @click.prevent="postMessage()"
              >
                <span
                  v-if="isPostingMessage"
                  class="loading loading-sm text-base-content/50"
                />
                <i v-else class="iconify mingcute--arrow-up-circle-fill" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="flex items-center text-center justify-center w-full">
        <div
          class="w-full h-full bg-base-300 flex items-center justify-center p-2 gap-2"
        >
          <span class="loading loading-xs loading-ring" />
          <span>{{ `正在与${mate?.name}建立连接...` }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
